import React, { useState, useEffect } from "react";
import { darkToast, toast, ToastContainer } from "../../Components/Toast";
import ReactLoading from "react-loading";
import { useHistory, useParams } from "react-router";
import axios from "axios";
import { serverUrl } from "../../Utils/server";
import FontAwesome from "react-fontawesome";

const EditProduct = () => {
  const { productId } = useParams();
  const history = useHistory();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [currencyCode, setCurrencyCode] = useState("USD");
  const [supplierPrice, setSupplierPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [promoPrice, setPromoPrice] = useState("");
  const [permiteCreditoFiscal, setPermiteCreditoFiscal] = useState(false);
  const [salePriceFiscal, setSalePriceFiscal] = useState("");
  const [promoPriceFiscal, setPromoPriceFiscal] = useState("");

  const [stock, setStock] = useState(0);
  const [weight, setWeight] = useState("");
  const [imageUrls, setImageUrls] = useState([]);

  const [categoryIndex, setCategoryIndex] = useState(-1);
  const [categoryId, setCategoryId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [tags, setTags] = useState("");

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [categories, setCategories] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [catalogCategoryIndex, setCatalogCategoryIndex] = useState(-1);
  const [catalogCategoryId, setCatalogCategoryId] = useState("");

  useEffect(() => {
    getCategories();
    getCatalogCategories();
    getProductDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!catalogCategoryId || catalogCategories.length === 0) return;
    const foundIndex = catalogCategories.findIndex((item) => item._id === catalogCategoryId);
    setCatalogCategoryIndex(foundIndex);
  }, [catalogCategoryId, catalogCategories]);

  const getCategories = () => {
    setLoading(true);
    axios({
      method: "get",
      url: `${serverUrl}/categories/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        setCategories(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      })
      .catch(() => {
        toast("Couldn't load categories. Please refresh.", darkToast);
        setLoading(false);
      });
  };

  const getCatalogCategories = () => {
    axios({
      method: "get",
      url: `${serverUrl}/catalog-categories/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        setCatalogCategories(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        toast("Couldn't load catalog categories.", darkToast);
      });
  };

  const getProductDetails = () => {
    setLoading(true);
    axios({
      method: "post",
      url: `${serverUrl}/products/get-product-by-id`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: { productId },
    })
      .then((response) => {
        const data = response.data || {};

        setTitle(data.name || data.title || "");
        setDescription(data.description || "");

        setCurrencyCode(data.currencyCode || "USD");
        setSupplierPrice(data.supplierPrice ?? "");
        setSalePrice(data.salePrice ?? data.price ?? "");
        setPromoPrice(data.promoPrice ?? "");
        setPermiteCreditoFiscal(Boolean(data.permiteCreditoFiscal));
        setSalePriceFiscal(data.salePriceFiscal ?? "");
        setPromoPriceFiscal(data.promoPriceFiscal ?? "");

        setStock(data.stock ?? 0);
        setWeight(data.weight || "");
        setImageUrls(Array.isArray(data.images) ? data.images : []);
        setTags(Array.isArray(data.tags) ? data.tags.join(", ") : "");

        setCategoryId(data.category?.id || "");
        setCategoryName(data.category?.name || "");
        setCategoryIndex(-1);

        const currentCatalogCategoryId =
          data.catalogCategoryId ||
          data.categoryRef ||
          data.categorySnapshot?.id ||
          "";

        setCatalogCategoryId(currentCatalogCategoryId);
        setLoading(false);
      })
      .catch(() => {
        toast("Failed to fetch product info", darkToast);
        setLoading(false);
      });
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingImages(true);

    try {
      for (const image of files) {
        const formData = new FormData();
        formData.append("image", image);

        const response = await axios.post(`${serverUrl}/upload/image`, formData, {
          headers: {
            Authorization: `JWT ${localStorage.getItem("token")}`,
          },
          timeout: 45000,
        });

        const url = response?.data?.url;
        if (!url) {
          throw new Error("Upload backend did not return url");
        }

        setImageUrls((prev) => [...prev, url]);
      }

      toast("Images uploaded successfully", darkToast);
    } catch (err) {
      toast(err?.response?.data?.msg || err?.message || "Error uploading image", darkToast);
    } finally {
      setUploadingImages(false);
    }
  };

  const updateProduct = () => {
    if (imageUrls.length === 0) {
      return toast("Upload atleast 1 image", darkToast);
    }

    if (salePrice === "" || Number(salePrice) <= 0) {
      return toast("Sale Price is required", darkToast);
    }

    if (promoPrice !== "" && Number(promoPrice) < 0) {
      return toast("Promo Price cannot be negative", darkToast);
    }

    if (supplierPrice !== "" && Number(supplierPrice) < 0) {
      return toast("Supplier Price cannot be negative", darkToast);
    }
    if (permiteCreditoFiscal && (salePriceFiscal === "" || Number(salePriceFiscal) <= 0)) {
      return toast("Sale Price Fiscal is required", darkToast);
    }

    if (promoPriceFiscal !== "" && Number(promoPriceFiscal) < 0) {
      return toast("Promo Price Fiscal cannot be negative", darkToast);
    }

    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/products/update`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        productId: productId,
        query: {
          title: title,
          name: title,
          description: description,
          currencyCode: currencyCode,
          supplierPrice: supplierPrice === "" ? null : Number(supplierPrice),
          salePrice: Number(salePrice),
          promoPrice: promoPrice === "" ? null : Number(promoPrice),
          permiteCreditoFiscal: permiteCreditoFiscal,
          salePriceFiscal: salePriceFiscal === "" ? null : Number(salePriceFiscal),
          promoPriceFiscal: promoPriceFiscal === "" ? null : Number(promoPriceFiscal),
          stock: Number(stock),
          weight: weight,
          images: imageUrls,
          category: {
            id: categoryIndex === -1 ? categoryId : categories[categoryIndex]?._id,
            name: categoryIndex === -1 ? categoryName : categories[categoryIndex]?.name,
          },
          catalogCategoryId:
            catalogCategoryIndex === -1
              ? catalogCategoryId
              : catalogCategories[catalogCategoryIndex]?._id || "",
          tags: tags.toString().split(",").map((t) => t.trim()).filter(Boolean),
        },
      },
    };

    axios(config)
      .then((response) => {
        if (response.status === 200) {
          toast("Product updated!", darkToast);

          setTimeout(() => {
            history.push("/products");
            window.location.reload();
          }, 1000);
        }
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.msg ||
          err?.response?.data ||
          err?.message ||
          "Something went wrong!";
        toast(String(msg), darkToast);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="content-body">
      <div className="content-title">
        <p>Edit Product</p>
      </div>
      <div className="form">
        <p>Product Title</p>
        <input
          type="text"
          placeholder="Product Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <p>Description</p>
        <input
          type="text"
          value={description}
          placeholder="Description"
          onChange={(e) => setDescription(e.target.value)}
        />

        <p>Currency Code</p>
        <select
          value={currencyCode}
          onChange={(e) => setCurrencyCode(e.target.value)}
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="NIO">NIO</option>
        </select>

        <p>Supplier Price</p>
        <input
          type="number"
          placeholder="Supplier Price"
          value={supplierPrice}
          onChange={(e) => setSupplierPrice(e.target.value)}
        />

        <p>Sale Price</p>
        <input
          type="number"
          placeholder="Sale Price"
          value={salePrice}
          onChange={(e) => setSalePrice(e.target.value)}
        />

        <p>Promo Price</p>
        <input
          type="number"
          placeholder="Promo Price"
          value={promoPrice}
          onChange={(e) => setPromoPrice(e.target.value)}
        />

        <p>Permite Crédito Fiscal</p>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <input
            type="checkbox"
            checked={permiteCreditoFiscal}
            onChange={(e) => setPermiteCreditoFiscal(e.target.checked)}
          />
          Sí, este producto maneja precio fiscal
        </label>

        <p>Sale Price Fiscal</p>
        <input
          type="number"
          placeholder="Sale Price Fiscal"
          value={salePriceFiscal}
          disabled={!permiteCreditoFiscal}
          onChange={(e) => setSalePriceFiscal(e.target.value)}
        />

        <p>Promo Price Fiscal</p>
        <input
          type="number"
          placeholder="Promo Price Fiscal"
          value={promoPriceFiscal}
          disabled={!permiteCreditoFiscal}
          onChange={(e) => setPromoPriceFiscal(e.target.value)}
        />

        <p>Stock</p>
        <input
          type="number"
          placeholder="Stock"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
        />

        <p>Weight</p>
        <input
          type="text"
          placeholder="Weight"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />

        <p>Images</p>
        {imageUrls.map((image, index) => {
          return (
            <div key={index} style={{ display: "inline-block", position: "relative" }}>
              <img src={image} alt="" style={{ width: "20%", margin: "1rem" }} />
              <button
                onClick={() => {
                  const urls = [...imageUrls];
                  urls.splice(index, 1);
                  setImageUrls(urls);
                }}
                style={{
                  position: "absolute",
                  top: "0.5rem",
                  right: "0.5rem",
                  background: "red",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <FontAwesome name="trash" />
              </button>
            </div>
          );
        })}

        <p>Add Image</p>
        {uploadingImages ? (
          <div style={{ margin: "1rem" }}>
            <ReactLoading
              type={"spinningBubbles"}
              color={"var(--primaryColor)"}
              width={"2rem"}
              height={"2rem"}
            />
          </div>
        ) : (
          <input type="file" onChange={uploadImages} />
        )}

        <p>Category</p>
        <select
          value={categoryIndex}
          onChange={(e) => setCategoryIndex(parseInt(e.target.value))}
        >
          <option value={-1}>
            {categoryName || "Select legacy category"}
          </option>
          {categories.map((category, index) => (
            <option key={index} value={index}>
              {category.name}
            </option>
          ))}
        </select>

        <p>Catalog Category</p>
        <select
          value={catalogCategoryIndex}
          onChange={(e) => setCatalogCategoryIndex(parseInt(e.target.value))}
        >
          <option value={-1}>
            {catalogCategories.find((c) => c._id === catalogCategoryId)?.name || "Select catalog category"}
          </option>
          {catalogCategories.map((category, index) => (
            <option key={index} value={index}>
              {category.name}
            </option>
          ))}
        </select>

        <p>Tags</p>
        <input
          type="text"
          placeholder="Tags separated by comma"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
        />

        {loading ? (
          <div style={{ margin: "1rem" }}>
            <ReactLoading
              type={"spinningBubbles"}
              color={"var(--primaryColor)"}
              width={"2rem"}
              height={"2rem"}
            />
          </div>
        ) : (
          <button onClick={updateProduct}>Update Product</button>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default EditProduct;
